import styled from "@emotion/styled";
import { FC } from "react";
import { useSelector } from "react-redux";

import { Button, Modal, ModalPortal, SpacingToken } from "@livechat/design-system-react-components";

import { Bar, BarType } from "components/bar";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { config } from "config/config";
import { selectLicenseId } from "store/entities/license";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { EmailPreview } from "./EmailPreview";

interface IProps {
  fromName: string | null;
  email: string;
  verified: boolean;
  onClose: () => void;
}

const PreviewAddressModal: FC<IProps> = ({ onClose, fromName, email, verified }) => {
  const licenseID = useSelector(selectLicenseId);
  const fallbackEmail = `${licenseID}@${config.defaultMailboxSuffix}`;
  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <Modal onClose={onClose} heading="Reply address preview" footer={<PreviewInboxButtons onClose={onClose} />}>
        <Flex maxWidth="520px" gap={withVar(SpacingToken.Spacing3)} column>
          <p>This is how your customers will see emails coming from this address.</p>
          {!verified && (
            <Block paddingBottom="16px">
              <Bar type={BarType.Info} iconAlign="start">
                Verifying{" "}
                <StyledText fontWeight="600" textOverflow="ellipsis" overflow="hidden" maxWidth="20vw" title={email}>
                  {email}
                </StyledText>{" "}
                reply address. Until verified, the{" "}
                <StyledText textOverflow="ellipsis" overflow="hidden" maxWidth="20vw" title={fallbackEmail}>
                  {fallbackEmail}
                </StyledText>{" "}
                address will be used.
              </Bar>
            </Block>
          )}
          <EmailPreview displayName={fromName || email} email={email} />
        </Flex>
      </Modal>
    </ModalPortal>
  );
};

interface IButtonsProps {
  onClose: () => void;
}

const PreviewInboxButtons: FC<IButtonsProps> = ({ onClose }) => (
  <Button kind="primary" size="large" onClick={onClose}>
    Done
  </Button>
);

const StyledText = styled(Text)`
  display: inline-block;
  vertical-align: bottom;
  white-space: nowrap;
`;

export default PreviewAddressModal;
